import { createContext, useCallback, useContext, useRef } from 'react'

import { runTrackedAction } from './trackedRun'
import { ActivitySubject } from './types'

/**
 * The subject (the record) the controls below it act on. Set by ActivitySubjectProvider, one per
 * case tab. Kept free of app imports so AppButton can read it without pulling in the stores.
 */
export const ActivitySubjectContext = createContext<ActivitySubject | null>(
  null
)

/** The subject of the nearest ActivitySubjectProvider, or null (tracking is off here). */
export const useActivitySubject = (): ActivitySubject | null =>
  useContext(ActivitySubjectContext)

/**
 * Runs `fn` as the tracked action `name` on the provider's subject and resolves with its result
 * (a promise returned by `fn` keeps the action open until it settles).
 */
export type TrackedActionRunner = <T>(
  name: string,
  fn: () => T | PromiseLike<T>
) => Promise<T>

/**
 * For actions that do not start on an AppButton (menu items, links, custom controls): returns a
 * stable runner bound to the provider's subject. Without a provider the action runs untracked.
 */
export const useTrackedAction = (): TrackedActionRunner => {
  const subject = useActivitySubject()
  const subjectRef = useRef(subject)
  subjectRef.current = subject

  return useCallback(<T>(name: string, fn: () => T | PromiseLike<T>) => {
    try {
      return Promise.resolve(runTrackedAction(name, subjectRef.current, fn))
    } catch (error) {
      return Promise.reject(error)
    }
  }, [])
}
